import { useMemo } from 'react';
import { getNavIcon } from '@/lib/navigation-icons';
import { useNavigationApi, resolveNodeRoute, resolveIcon } from './useNavigationApi';
import { type LucideIcon } from 'lucide-react';
import { getStoredBrand } from '@/lib/brandThemes';

export interface SidebarLink {
  title: string;
  url: string;
  icon: LucideIcon;
}

const THOR_HIDDEN_SIDEBAR_TAGS = [
  'PartsSearch_Link',
  'DealersOrders_Link',
  'PartsInvoice_Link',
  'Spiffs_Link',
  'ClaimsAndPreAuths_Link',
  'WarrantyRegistration_Link',
];

const THOR_HIDDEN_SIDEBAR_TITLES = [
  'Parts Search',
  'Dealer Orders',
  'Parts Invoice',
  'Spiffs',
  'Claims and Pre-Auths',
  'Claims & Pre-Auths',
  'Warranty Registration',
];

const mockQuickLinks: SidebarLink[] = [
  { title: 'VIN/Unit Search', url: '/units', icon: getNavIcon('UnitSearch_Link') },
  { title: 'Parts Search', url: '#', icon: getNavIcon('PartsSearch_Link') },
  { title: 'Dealer Orders', url: '/orders', icon: getNavIcon('DealersOrders_Link') },
  { title: 'Parts Invoice', url: '#', icon: getNavIcon('PartsInvoice_Link') },
  { title: 'Reports', url: '#', icon: getNavIcon('Reports_Link') },
  { title: 'Spiffs', url: '#', icon: getNavIcon('Spiffs_Link') },
  { title: 'Claims and Pre-Auths', url: '/dealer-claims', icon: getNavIcon('ClaimsAndPreAuths_Link') },
  { title: 'Warranty Registration', url: '/warranty-registration', icon: getNavIcon('WarrantyRegistration_Link') },
  { title: 'Service Campaigns', url: '/service-campaigns', icon: getNavIcon('ServiceCampaigns_Link') },
];

export function useSidebarNavigation() {
  const { nodes, isLoading, useMock } = useNavigationApi({
    rootNavigationTag: 'QuickLinks',
  });

  const isThor = getStoredBrand() === 'Thor';

  const apiLinks = useMemo<SidebarLink[] | null>(() => {
    if (!nodes) return null;

    return nodes
      .filter((node: any) => node.showInMenu !== false)
      .filter((node: any) => {
        if (!isThor) return true;
        const navigationTag = node.navigationTag || '';
        const title = node.name || '';
        return !THOR_HIDDEN_SIDEBAR_TAGS.includes(navigationTag) && !THOR_HIDDEN_SIDEBAR_TITLES.includes(title);
      })
      .map((node: any) => {
        const title = node.name || '';
        return {
          title,
          url: resolveNodeRoute(node),
          icon: resolveIcon(node.navigationTag),
        };
      });
  }, [nodes, isThor]);

  const filteredMockLinks = useMemo(() => {
    if (!isThor) return mockQuickLinks;
    return mockQuickLinks.filter((link) => !THOR_HIDDEN_SIDEBAR_TITLES.includes(link.title));
  }, [isThor]);

  return {
    quickLinks: useMock ? filteredMockLinks : (apiLinks ?? []),
    isLoading: !useMock && isLoading,
    isMock: useMock,
  };
}
